// 已选文件集合的唯一真相源。
//
// 为什么存在:上传区文案写着"拖拽多张图片""最多 200 个文件",而各页此前一律
// `onFiles={setFiles}` —— 每次多选 / 每次拖拽都是**整体覆盖**,第二次拖拽会把第一次
// 选的全部顶掉,而且选错了也没有任何删除入口,只能整页刷新重来。
// 这里把"累加 + 去重 + 单个删除 + 清空"收成一处,各页只调用,不再各写各的。
import { useCallback, useRef, useState } from "react";

/** 去重身份:浏览器不给 File 稳定 id,name + size + lastModified 是最接近的等价物。 */
export function fileKey(f: File): string {
  return `${f.name}|${f.size}|${f.lastModified}`;
}

/**
 * 把新选中的文件追加到已选列表,丢弃与已选重复的项。
 * 返回 [合并后的列表, 本次被丢弃的重复数, 因数量上限未加入的文件数]。
 * 没有任何新增时原样返回 prev(引用不变,避免无谓重渲染)。
 */
export function mergeFiles(prev: File[], incoming: File[], maxFiles = Number.POSITIVE_INFINITY): [File[], number, number] {
  const seen = new Set(prev.map(fileKey));
  const added: File[] = [];
  let duplicates = 0;
  let overflow = 0;
  for (const f of incoming) {
    const k = fileKey(f);
    if (seen.has(k)) {
      duplicates += 1;
      continue;
    }
    if (prev.length + added.length >= maxFiles) {
      overflow += 1;
      continue;
    }
    seen.add(k);
    added.push(f);
  }
  return [added.length > 0 ? [...prev, ...added] : prev, duplicates, overflow];
}

export interface FileSelection {
  files: File[];
  /** 追加(多选 / 多次拖拽都累加,不覆盖);重复文件自动跳过。直接接到 UploadDropzone 的 onFiles。 */
  add: (incoming: File[]) => void;
  /** 删除第 index 个 */
  remove: (index: number) => void;
  /** 清空 */
  clear: () => void;
  /** 覆盖式设置:只给"注入内置示例""回放重置"这类确实要整体替换的场景用。 */
  replace: (files: File[]) => void;
  /** 最近一次 add 因重复被跳过的文件数(供 UI 提示;下一次 add / 清空后归零) */
  duplicates: number;
  /** 最近一次 add 因达到累计数量上限而未加入的文件数 */
  overflow: number;
}

/**
 * 累加式文件选择。
 * 内部用 ref 镜像当前列表,所以 add 里能同步算出"跳过了几个重复"而不必把副作用塞进
 * setState 的函数式更新器(StrictMode 下会被双调用)。所有写入都过 commit,ref 不会漂。
 */
export function useFileSelection(initial: File[] = [], maxFiles = Number.POSITIVE_INFINITY): FileSelection {
  const [files, setFiles] = useState<File[]>(initial);
  const [duplicates, setDuplicates] = useState(0);
  const [overflow, setOverflow] = useState(0);
  const ref = useRef<File[]>(initial);

  const commit = useCallback((next: File[]) => {
    ref.current = next;
    setFiles(next);
  }, []);

  const add = useCallback(
    (incoming: File[]) => {
      const [next, dup, over] = mergeFiles(ref.current, incoming, maxFiles);
      setDuplicates(dup);
      setOverflow(over);
      commit(next);
    },
    [commit, maxFiles]
  );

  const remove = useCallback(
    (index: number) => {
      setDuplicates(0);
      setOverflow(0);
      commit(ref.current.filter((_, i) => i !== index));
    },
    [commit]
  );

  const clear = useCallback(() => {
    setDuplicates(0);
    setOverflow(0);
    commit([]);
  }, [commit]);

  const replace = useCallback(
    (next: File[]) => {
      setDuplicates(0);
      setOverflow(Math.max(0, next.length - maxFiles));
      commit(next.slice(0, maxFiles));
    },
    [commit, maxFiles]
  );

  return { files, add, remove, clear, replace, duplicates, overflow };
}

/** 人话文件大小(列表里逐个显示 + 合计)。 */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** 合计字节数 */
export function totalBytes(files: File[]): number {
  return files.reduce((s, f) => s + f.size, 0);
}
